/**
 * API client — single source for backend calls.
 *
 * Auth: injects the localStorage Bearer token when present, and always sends the
 * httpOnly session cookie (withCredentials) so a request still authenticates
 * after iOS/Safari evicts localStorage. Auto-redirects to /login on 401 — unless
 * the caller opts out via `__noAuthRedirect` (used by the cookie bootstrap).
 */
import axios from 'axios'
import { locale } from '../i18n/index.js'

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hr_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  // The UI language — the server answers its messages and documents in it.
  config.headers['X-Lang'] = locale
  // LiteSpeed (prod reverse proxy) rejects bodyless POST/PUT/PATCH with a bare
  // 400 before the request ever reaches Express. Guarantee an empty-object body
  // so action endpoints like /leaves/:id/approve always go through.
  const m = (config.method || 'get').toLowerCase()
  if ((m === 'post' || m === 'put' || m === 'patch') && config.data == null) {
    config.data = {}
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !err.config?.__noAuthRedirect && location.pathname !== '/login') {
      localStorage.removeItem('hr_token')
      localStorage.removeItem('hr_user')
      location.replace('/login')
    }
    return Promise.reject(err)
  }
)

export default api
